"use client";
import { useCallback, useEffect, useRef, useState } from "react"
import {
  TableMap,
  addColumnAfter as pmAddColumnAfter,
  addRowAfter as pmAddRowAfter,
} from "@tiptap/pm/tables"
import { FloatingPortal } from "@floating-ui/react"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import { useTableHandleState } from "@/tiptap/nodes/table-node/hooks/use-table-handle-state"

import {
  EMPTY_CELL_HEIGHT,
  EMPTY_CELL_WIDTH,
  countEmptyRowsFromEnd,
  countEmptyColumnsFromEnd,
  marginRound,
  selectLastCell,
  runPreservingCursor,
} from "@/tiptap/nodes/table-node/lib/tiptap-table-utils"
import { cn } from "@/tiptap/lib/tiptap-utils"

// --- Icons ---
import { PlusSmallIcon } from "@/tiptap/icons/plus-small-icon"

// --- Internal ---
import { useTableExtendRowColumnButtonsPositioning } from "@/tiptap/nodes/table-node/ui/table-extend-row-column-button/use-table-extend-row-column"

function getLiveTableNode(editor, tableBlockPos) {
  if (!editor || tableBlockPos === undefined) return null

  const node = editor.state.doc.nodeAt(tableBlockPos)
  if (!node || node.type.name !== "table") return null

  return node
}

/**
 * How many rows/columns to add or remove for the current drag distance.
 * `currentCount` must come from the live table — a stale snapshot makes every
 * mousemove re-apply the full delta and spam tiny columns/rows.
 */
export function getTableExtendDelta({
  diff,
  cellSize,
  originalCount,
  currentCount,
}) {
  const newCount = Math.max(1, originalCount + marginRound(diff / cellSize, 0.3))
  return newCount - currentCount
}

/**
 * Simplified button component for extending/reducing table dimensions.
 *
 * Receives `tableBlock` and `tableBlockPos` as props — these are captured
 * by the parent when the button first becomes visible, so they always
 * reference the correct table even if the plugin state later changes
 * (e.g. when the mouse crosses a parent cell gap in nested tables).
 *
 * During drag, dimensions are always read from the live doc at
 * `tableBlockPos` so already-added rows/columns are not added again.
 */
export const TableExtendRowColumnButton = ({
  editor: providedEditor,
  onMouseDown,
  onMouseUp,
  orientation,
  tableBlock,
  tableBlockPos,
  children,
}) => {
  const { editor } = useTiptapEditor(providedEditor)
  const isRowOrientation = orientation === "row"

  const movedRef = useRef(false)
  const [dragState, setDragState] = useState(null)

  const startDrag = useCallback((ev) => {
    const liveTable = getLiveTableNode(editor, tableBlockPos) || tableBlock
    if (!liveTable) return

    const dims = TableMap.get(liveTable)
    movedRef.current = false

    setDragState({
      startPos: isRowOrientation ? ev.clientY : ev.clientX,
      originalHeight: dims.height,
      originalWidth: dims.width,
    })

    onMouseDown()
    ev.preventDefault()
  }, [editor, tableBlock, tableBlockPos, isRowOrientation, onMouseDown])

  const handleClick = useCallback(() => {
    if (movedRef.current || !editor || tableBlockPos === undefined) return

    const liveTable = getLiveTableNode(editor, tableBlockPos) || tableBlock
    if (!liveTable) return

    runPreservingCursor(editor, () => {
      selectLastCell(editor, liveTable, tableBlockPos, orientation)

      if (isRowOrientation) {
        pmAddRowAfter(editor.view.state, editor.view.dispatch)
      } else {
        pmAddColumnAfter(editor.view.state, editor.view.dispatch)
      }
    })
  }, [editor, isRowOrientation, orientation, tableBlock, tableBlockPos])

  useEffect(() => {
    if (!dragState || !editor || tableBlockPos === undefined) return

    const handleMove = (ev) => {
      movedRef.current = true

      const liveTable = getLiveTableNode(editor, tableBlockPos)
      if (!liveTable) return

      const currentPos = isRowOrientation ? ev.clientY : ev.clientX
      const diff = currentPos - dragState.startPos
      const cellSize = isRowOrientation ? EMPTY_CELL_HEIGHT : EMPTY_CELL_WIDTH

      const currentDims = TableMap.get(liveTable)
      const currentCount = isRowOrientation
        ? currentDims.height
        : currentDims.width
      const originalCount = isRowOrientation
        ? dragState.originalHeight
        : dragState.originalWidth

      const delta = getTableExtendDelta({
        diff,
        cellSize,
        originalCount,
        currentCount,
      })

      if (delta === 0) return

      // Add rows/columns
      if (delta > 0) {
        runPreservingCursor(editor, () => {
          // Re-read before each mutation batch — the table node updates as we add.
          const tableForSelect = getLiveTableNode(editor, tableBlockPos)
          if (!tableForSelect) return

          selectLastCell(editor, tableForSelect, tableBlockPos, orientation)

          for (let i = 0; i < delta; i++) {
            if (isRowOrientation) {
              pmAddRowAfter(editor.view.state, editor.view.dispatch)
            } else {
              pmAddColumnAfter(editor.view.state, editor.view.dispatch)
            }
          }
        })
      }
      // Remove rows/columns - but only if they're empty
      else {
        runPreservingCursor(editor, () => {
          const absDelta = Math.abs(delta)

          const emptyCount = isRowOrientation
            ? countEmptyRowsFromEnd(editor, tableBlockPos)
            : countEmptyColumnsFromEnd(editor, tableBlockPos)

          // Only remove up to the number of empty cells, and keep at least 1
          const safeToRemove = Math.min(absDelta, emptyCount, currentCount - 1)
          if (safeToRemove <= 0) return

          const tableForSelect = getLiveTableNode(editor, tableBlockPos)
          if (!tableForSelect) return

          selectLastCell(editor, tableForSelect, tableBlockPos, orientation)

          for (let i = 0; i < safeToRemove; i++) {
            if (isRowOrientation) {
              editor.commands.deleteRow()
            } else {
              editor.commands.deleteColumn()
            }
          }
        })
      }
    }

    const handleUp = () => {
      setDragState(null)
      onMouseUp()
    }

    window.addEventListener("mousemove", handleMove)
    window.addEventListener("mouseup", handleUp)

    return () => {
      window.removeEventListener("mousemove", handleMove)
      window.removeEventListener("mouseup", handleUp)
    };
  }, [dragState, editor, isRowOrientation, orientation, onMouseUp, tableBlockPos])

  if (!editor?.isEditable) return null

  return (
    <button
      className={cn("tiptap-table-extend-row-column-button", isRowOrientation
        ? "tiptap-table-row-end-add-remove"
        : "tiptap-table-column-end-add-remove", dragState && "editing")}
      onClick={handleClick}
      onMouseDown={startDrag}
      type="button"
      aria-label={
        isRowOrientation ? "Add or remove rows" : "Add or remove columns"
      }>
      {children ?? <PlusSmallIcon className="tiptap-button-icon" />}
    </button>
  );
}

export const TableExtendRowColumnButtons = ({ editor: providedEditor, onMouseDown, onMouseUp }) => {
  const { editor } = useTiptapEditor(providedEditor)
  const state = useTableHandleState({ editor })

  // Capture the table identity when either extend button first becomes
  // visible.  At that moment the plugin state is correct (the user is
  // hovering the last row/column of the target table).  As the mouse
  // later crosses intermediate DOM zones toward the button, the plugin
  // state may switch to a parent table, but these captured values stay
  // pinned to the original table.
  const capturedRef = useRef(null)
  const showRow = state?.showAddOrRemoveRowsButton ?? false
  const showCol = state?.showAddOrRemoveColumnsButton ?? false

  if ((showRow || showCol) && state?.block && state?.blockPos !== undefined) {
    const newRows = state.block.content?.childCount ?? 0
    const newCols = state.block.content?.firstChild?.content?.childCount ?? 0
    const newSize = newRows * newCols

    const curRows = capturedRef.current?.block?.content?.childCount ?? 0
    const curCols = capturedRef.current?.block?.content?.firstChild?.content?.childCount ?? 0
    const curSize = capturedRef.current ? curRows * curCols : Infinity

    if (newSize <= curSize) {
      capturedRef.current = { block: state.block, blockPos: state.blockPos }
    }
  } else {
    capturedRef.current = null
  }

  const captured = capturedRef.current

  const scrollWrapper =
    state?.widgetContainer?.parentElement?.querySelector(":scope > .tableWrapper-scroll") ??
    null

  const { columnButton, rowButton } = useTableExtendRowColumnButtonsPositioning(
    showCol,
    showRow,
    state?.referencePosTable ?? null,
    scrollWrapper,
  )

  const handleDown = useCallback(() => {
    if (!editor) return
    editor.commands.freezeHandles()
    onMouseDown?.()
  }, [editor, onMouseDown])

  const handleUp = useCallback(() => {
    if (!editor) return
    editor.commands.unfreezeHandles()
    onMouseUp?.()
  }, [editor, onMouseUp])

  if (!state) return null

  return (
    <FloatingPortal root={state.widgetContainer}>
      <div ref={rowButton.ref} style={rowButton.style}>
        <TableExtendRowColumnButton
          editor={editor}
          orientation="row"
          tableBlock={captured?.block}
          tableBlockPos={captured?.blockPos}
          onMouseDown={handleDown}
          onMouseUp={handleUp} />
      </div>
      <div ref={columnButton.ref} style={columnButton.style}>
        <TableExtendRowColumnButton
          editor={editor}
          orientation="column"
          tableBlock={captured?.block}
          tableBlockPos={captured?.blockPos}
          onMouseDown={handleDown}
          onMouseUp={handleUp} />
      </div>
    </FloatingPortal>
  );
}
